import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useFlowState } from "@/hooks/useFlowState";
import {
    useModelPageState,
    useModelChangeError,
    useCurrentModelChange,
    useCurrentRapidData,
    useMarkCurrentChangeAsSeen,
    useClearMediaState
} from "@/store";
import { useUser } from "@/contexts/UserContext";
import { DeploySteps, Flows } from "@/types/flows";
import { useModelPage } from "./hooks/useModelPage";
import { ModelPageState } from "./types";
import { ModelComparisonView } from "./ModelComparisonView";
import { ModelEmptyState, ModelErrorState } from "./components/ModelEmptyState";
import LoadingScreen from "@/components/LoadingScreen";
import { FullScreenModal } from "@/components/flow/shared/FullScreenModal";

export function ModelModal() {
    const navigate = useNavigate();
    const { computeUrlPath } = useFlowState();

    useModelPage();

    const pageState = useModelPageState();
    const error = useModelChangeError();
    const currentChange = useCurrentModelChange();
    const rapidData = useCurrentRapidData();
    const markAsSeen = useMarkCurrentChangeAsSeen();
    const clearMediaState = useClearMediaState();
    const user = useUser();

    const markAsSeenRef = useRef(markAsSeen);
    const rapidDataIdRef = useRef(rapidData?.id);
    const userIdRef = useRef(user?.uid);
    const currentChangeRef = useRef(currentChange);

    useEffect(() => {
        markAsSeenRef.current = markAsSeen;
        rapidDataIdRef.current = rapidData?.id;
        userIdRef.current = user?.uid;
        currentChangeRef.current = currentChange;
    });

    useEffect(() => {
        return () => {
            const id = rapidDataIdRef.current;
            const uid = userIdRef.current;
            if (id && uid && currentChangeRef.current) {
                markAsSeenRef.current(id, uid);
            }
        };
    }, []);

    // Clear media state when unmounting (leaving the page).
    // Model state is intentionally preserved — MetricsSnippet (on the deploy
    // page) reads currentModelChange and currentModelMetrics from the store.
    // Clearing them here would cause a skeleton flash until DeployProvider
    // re-initializes. The next modal open gets a fresh useRef guard, so
    // initializeModel re-runs with current Firestore data.
    useEffect(() => {
        return () => {
            clearMediaState();
        };
    }, [clearMediaState]);

    const handleClose = useCallback(() => {
        const deployPath = computeUrlPath({ flow: Flows.DEPLOY, step: DeploySteps.DEPLOY });
        navigate(deployPath);
    }, [navigate, computeUrlPath]);

    const renderContent = () => {
        if (error) {
            return (
                <div className="flex h-full items-center justify-center">
                    <div className="text-center">
                        <p className="text-red-600">Error: {error}</p>
                    </div>
                </div>
            );
        }

        switch (pageState) {
            case ModelPageState.LOADING:
                // If rapidData has loaded but there's no model, show empty state
                if (rapidData && !currentChange) {
                    return <ModelEmptyState />;
                }
                return <LoadingScreen message="Loading model results..." />;

            case ModelPageState.NEW_MODEL_COMPARISON:
            case ModelPageState.SINGLE_MODEL_PERFORMANCE:
            case ModelPageState.HISTORICAL_COMPARISON:
                return <ModelComparisonView />;

            case ModelPageState.ERROR:
                return <ModelErrorState />;

            default:
                return <LoadingScreen message="Loading..." />;
        }
    };

    return (
        <FullScreenModal
            title="Model Overview"
            description="View your trained model's performance and metrics."
            onClose={handleClose}
            showHeaderBorder={false}
        >
            {renderContent()}
        </FullScreenModal>
    );
}
